import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ChevronRight, Loader2, Car, Bike, Truck, Check, X, Search } from 'lucide-react';
import { useAppData } from '@/contexts/AppDataContext';
import { API_FIPE } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTheme } from '@/contexts/ThemeContext';
import { useStore } from '@/contexts/StoreContext';
import { layout4ThemeStyle } from '@/layouts/layout-4/theme';
import '@/layouts/layout-4/styles.css';

interface FipeVehicleSelectorProps {
  service: any;
  value: string;
  onChange: (vehicleText: string, vehicleData: { brand: string; model: string; year: string; brandId?: string; modelId?: string; yearId?: string; vehicleType?: number }) => void;
  serviceImage?: string;
  serviceTitle?: string;
  layoutVariant?: 'default' | 'layout-4';
}

interface VehicleData {
  brand: { _id: string | null; label: string | null };
  model: { _id: string | null; label: string | null };
  year: { _id: string | null; label: string | null };
}

export const FipeVehicleSelector = ({ service, value, onChange, serviceImage, serviceTitle, layoutVariant = 'default' }: FipeVehicleSelectorProps) => {
  const { config } = useAppData();
  const { selectedStore } = useStore();
  const store = selectedStore || config.stores[0];

  const { colors } = useTheme();

  const buttonColor = colors.button;
  const primaryColor = colors.primary;
  const secondaryColor = colors.secondary;
  const titleColor = colors.title;
  const textColor = colors.text;
  const isLayout4 = layoutVariant === 'layout-4';
  const modalThemeStyle = isLayout4 ? layout4ThemeStyle(colors) : undefined;

  const availableTypes = useMemo(() => {
    // Always show all vehicle types for Avaliação
    const types: Array<{ label: string; value: number; icon: any }> = [
      { label: 'Carro', value: 1, icon: Car },
      { label: 'Moto', value: 2, icon: Bike },
      { label: 'Caminhão', value: 3, icon: Truck }
    ];

    return types;
  }, []);

  const [vehicleType, setVehicleType] = useState(availableTypes[0]?.value || 1);
  const [mainModalOpen, setMainModalOpen] = useState(false);
  const [data, setData] = useState<VehicleData>({
    brand: { _id: null, label: null },
    model: { _id: null, label: null },
    year: { _id: null, label: null },
  });
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectorField, setSelectorField] = useState<'brand' | 'model' | 'year'>('brand');

  // Update parent component when vehicle is fully selected
  useEffect(() => {
    if (data.brand.label && data.model.label && data.year.label) {
      const vehicleText = `${data.brand.label} ${data.model.label} ${data.year.label}`;
      onChange(vehicleText, {
        brand: data.brand.label,
        model: data.model.label,
        year: data.year.label,
        brandId: data.brand._id || undefined,
        modelId: data.model._id || undefined,
        yearId: data.year._id || undefined,
        vehicleType: vehicleType
      });
    }
  }, [data.brand.label, data.model.label, data.year.label, data.brand._id, data.model._id, data.year._id, vehicleType, onChange]);

  const handleVehicleTypeChange = (newType: number) => {
    setVehicleType(newType);
    setData({
      brand: { _id: null, label: null },
      model: { _id: null, label: null },
      year: { _id: null, label: null },
    });
  };

  const handleFieldSelect = (field: 'brand' | 'model' | 'year') => {
    setSelectorField(field);
    setSelectorOpen(true);
  };

  const handleValueSelect = (field: 'brand' | 'model' | 'year', selectedValue: { _id: string; label: string }) => {
    if (field === 'brand') {
      setData({
        brand: selectedValue,
        model: { _id: null, label: null },
        year: { _id: null, label: null },
      });
    } else if (field === 'model') {
      setData(prev => ({
        ...prev,
        model: selectedValue,
        year: { _id: null, label: null },
      }));
    } else {
      setData(prev => ({
        ...prev,
        year: selectedValue,
      }));
    }
    setSelectorOpen(false);
  };

  const getDisplayValue = () => {
    if (data.brand.label && data.model.label && data.year.label) {
      return `${data.brand.label} ${data.model.label} ${data.year.label}`;
    }
    return 'Selecionar';
  };

  // Get icon based on vehicle type
  const getVehicleTypeIcon = () => {
    const typeData = availableTypes.find(t => t.value === vehicleType);
    return typeData?.icon || Car;
  };

  return (
    <>
      <div
        onClick={() => setMainModalOpen(true)}
        className={
          isLayout4
            ? 'wl4-vehicle-picker layout-4'
            : 'flex items-center gap-4 p-4 border rounded-lg transition-all border-border cursor-pointer hover:bg-muted/50 hover:border-primary/50'
        }
        style={isLayout4 ? modalThemeStyle : undefined}
      >
        {data.brand.label && data.model.label && data.year.label ? (
          <>
            {/* Vehicle Image Placeholder */}
            <div className="relative flex-shrink-0">
              <div className="w-16 h-12 bg-muted/50 rounded-md flex items-center justify-center">
                {(() => {
                  const VehicleIcon = getVehicleTypeIcon();
                  return <VehicleIcon className="h-6 w-6 text-muted-foreground/40" />;
                })()}
              </div>
            </div>

            {/* Vehicle Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-2">
                <h4 className="font-semibold text-sm truncate">
                  {data.brand.label} {data.model.label}
                </h4>
              </div>
              
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                {data.year.label}
              </p>

              {/* Minimalist Details */}
              <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                <span>FIPE</span>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Placeholder Image */}
            <div className="relative flex-shrink-0">
              <div className="w-16 h-12 bg-muted/50 rounded-md flex items-center justify-center">
                {(() => {
                  const VehicleIcon = getVehicleTypeIcon();
                  return <VehicleIcon className="h-6 w-6 text-muted-foreground/40" />;
                })()}
              </div>
            </div>

            {/* Placeholder Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-2">
                <h4 className="font-semibold text-sm truncate">
                  VEÍCULO
                </h4>
                {/* <div className="h-4 bg-muted/50 rounded w-20" style={{ background: primaryColor, opacity: 0.10 }}></div> */}
              </div>
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                CLIQUE PARA SELECIONAR
              </p>

              {/* Minimalist Details */}
              <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                <div className="h-4 bg-muted/50 rounded w-28"></div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Main Selection Modal */}
      <Dialog open={mainModalOpen} onOpenChange={setMainModalOpen}>
        <DialogContent
          className={
            isLayout4
              ? 'layout-4 wl4-service-modal max-w-md w-full h-full md:max-h-[90vh] p-0 flex flex-col gap-0 [&>button]:hidden overflow-hidden'
              : 'max-w-md w-full h-full md:max-h-[90vh] p-0 flex flex-col gap-0 [&>button]:hidden overflow-hidden'
          }
          style={modalThemeStyle}
        >
          <DialogTitle className="sr-only">
            {serviceTitle || 'Selecionar Veículo'}
          </DialogTitle>

          {isLayout4 ? (
            <div className="wl4-service-modal-head">
              <div className="wl4-service-modal-head-icon">
                {(() => {
                  const VehicleIcon = getVehicleTypeIcon();
                  return <VehicleIcon className="h-5 w-5" />;
                })()}
              </div>
              <div className="wl4-service-modal-head-text">
                <h2>Selecionar veículo FIPE</h2>
                <p>Informe marca, modelo e ano</p>
              </div>
              <button
                type="button"
                className="wl4-service-modal-close"
                onClick={() => setMainModalOpen(false)}
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : serviceImage ? (
            <div className="relative aspect-[280/100] overflow-hidden flex-shrink-0">
              {/* Close Button */}
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-4 right-4 h-10 w-10 bg-black/5 hover:bg-black/10 backdrop-blur-sm border border-black/10 text-black z-20"
                onClick={() => setMainModalOpen(false)}
              >
                <X className="h-5 w-5 text-black" />
              </Button>

              {/* Title and Icon Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-black/5 backdrop-blur-sm border border-black/10">
                    {(() => {
                      const VehicleIcon = getVehicleTypeIcon();
                      return <VehicleIcon className="h-7 w-7 text-black/80" />;
                    })()}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-black">
                      Selecionar Veículo
                    </h2>
                    <p className="text-sm text-black/80">
                      Escolha pela tabela FIPE
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-slate-100 via-slate-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
              {/* Hidden DialogTitle for accessibility */}
              <DialogTitle className="sr-only">
                {serviceTitle || 'Selecionar Veículo'}
              </DialogTitle>

              {/* Gradient Overlay */}
              <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${primaryColor}15, ${primaryColor}05)` }} />
              
              {/* Close Button */}
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-4 right-4 h-10 w-10 bg-background/10 hover:bg-background/20 backdrop-blur-sm border border-border/20 z-20"
                onClick={() => setMainModalOpen(false)}
              >
                <X className="h-5 w-5" />
              </Button>

              {/* Title and Icon Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-background/10 backdrop-blur-sm border border-border/20">
                    {(() => {
                      const VehicleIcon = getVehicleTypeIcon();
                      return <VehicleIcon className="h-7 w-7" style={{ color: primaryColor }} />;
                    })()}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-foreground">
                      Selecionar Veículo
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Escolha pela tabela FIPE
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className={isLayout4 ? 'wl4-service-modal-body' : 'flex-1 overflow-y-auto px-6 py-6'}>
            {/* Vehicle Type Selector */}
            <div className={isLayout4 ? 'wl4-fipe-tabs mb-4' : 'mb-4'}>
              <Tabs value={String(vehicleType)} onValueChange={(v) => handleVehicleTypeChange(Number(v))}>
                <TabsList className="grid w-full grid-cols-3">
                  {availableTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <TabsTrigger key={type.value} value={String(type.value)} className="flex items-center gap-2">
                        <Icon className="h-4 w-4" />
                        {type.label}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              </Tabs>
            </div>

            {/* Selection Fields - Minimalist */}
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => handleFieldSelect('brand')}
                className={isLayout4 ? 'wl4-fipe-step' : 'w-full flex items-center justify-between p-4 rounded-lg hover:bg-muted/50 transition-all text-left border border-border'}
              >
                <div className="flex-1">
                  <p className={isLayout4 ? 'wl4-fipe-step-label' : 'text-xs text-muted-foreground mb-1'}>Marca</p>
                  <p className={isLayout4 ? 'wl4-fipe-step-value' : 'text-sm font-semibold'}>
                    {data.brand.label || 'Selecionar'}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </button>

              <button
                type="button"
                onClick={() => handleFieldSelect('model')}
                disabled={!data.brand._id}
                className={isLayout4 ? 'wl4-fipe-step' : 'w-full flex items-center justify-between p-4 rounded-lg hover:bg-muted/50 transition-all text-left border border-border disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent'}
              >
                <div className="flex-1">
                  <p className={isLayout4 ? 'wl4-fipe-step-label' : 'text-xs text-muted-foreground mb-1'}>Modelo</p>
                  <p className={isLayout4 ? 'wl4-fipe-step-value' : 'text-sm font-semibold'}>
                    {data.model.label || 'Selecionar'}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </button>

              <button
                type="button"
                onClick={() => handleFieldSelect('year')}
                disabled={!data.model._id}
                className={isLayout4 ? 'wl4-fipe-step' : 'w-full flex items-center justify-between p-4 rounded-lg hover:bg-muted/50 transition-all text-left border border-border disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent'}
              >
                <div className="flex-1">
                  <p className={isLayout4 ? 'wl4-fipe-step-label' : 'text-xs text-muted-foreground mb-1'}>Ano</p>
                  <p className={isLayout4 ? 'wl4-fipe-step-value' : 'text-sm font-semibold'}>
                    {data.year.label || 'Selecionar'}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>
          </div>

          {/* Confirm Button - Fixed Footer */}
          {data.brand.label && data.model.label && data.year.label && (
            <div className={isLayout4 ? 'wl4-service-modal-footer' : 'sticky bottom-0 z-10 bg-background border-t px-6 py-4'}>
              <Button
                onClick={() => setMainModalOpen(false)}
                className={isLayout4 ? 'wl4-service-modal-submit' : 'w-full h-11 text-white transition-all duration-200'}
                style={isLayout4 ? undefined : { backgroundColor: buttonColor }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                Confirmar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Selector Dialog */}
      {selectorOpen && (
        <FipeOptionSelector
          isOpen={selectorOpen}
          onOpenChange={setSelectorOpen}
          field={selectorField}
          vehicleType={vehicleType}
          fieldValue={data}
          onSelect={handleValueSelect}
          serviceImage={serviceImage}
          serviceTitle={serviceTitle}
          layoutVariant={layoutVariant}
        />
      )}
    </>
  );
};

// FipeOptionSelector Component
interface FipeOptionSelectorProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  field: 'brand' | 'model' | 'year';
  vehicleType: number;
  fieldValue: VehicleData;
  onSelect: (field: 'brand' | 'model' | 'year', value: { _id: string; label: string }) => void;
  serviceImage?: string;
  serviceTitle?: string;
  layoutVariant?: 'default' | 'layout-4';
}

const FipeOptionSelector = ({
  isOpen,
  onOpenChange,
  field,
  vehicleType,
  fieldValue,
  onSelect,
  serviceImage,
  serviceTitle,
  layoutVariant = 'default',
}: FipeOptionSelectorProps) => {
  const { config } = useAppData();
  const { selectedStore } = useStore();
  const store = selectedStore || config.stores[0];
  const [search, setSearch] = useState('');
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { colors } = useTheme();
  const isLayout4 = layoutVariant === 'layout-4';
  const modalThemeStyle = isLayout4 ? layout4ThemeStyle(colors) : undefined;

  const primaryColor = colors.primary;

  const fieldLabels = {
    brand: 'Marca',
    model: 'Modelo',
    year: 'Ano'
  };

  // Get icon based on vehicle type
  const getVehicleTypeIcon = () => {
    const iconMap: Record<number, any> = {
      1: Car,    // Carro
      2: Bike,   // Moto
      3: Truck   // Caminhão
    };
    return iconMap[vehicleType] || Car;
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Determine which parameters to send based on the field being selected
        let brandId = null;
        let modelId = null;

        if (field === 'model') {
          // For models, send brand
          brandId = fieldValue.brand._id;
        } else if (field === 'year') {
          // For years, send brand and model
          brandId = fieldValue.brand._id;
          modelId = fieldValue.model._id;
        }
        // For brand, send nothing (brandId and modelId remain null)

        const response = await API_FIPE.get(
          store,
          String(vehicleType),
          brandId,
          modelId,
          null
        );

        // Response comes as { status: 200, data: [...] }
        if (response && response.status === 200 && response.data) {
          if (Array.isArray(response.data)) {
            setData(response.data);
          } else {
            setData([]);
          }
        } else {
          setData([]);
        }
      } catch (error) {
        console.error('❌ Error:', error);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen, vehicleType, field, fieldValue.brand._id, fieldValue.model._id]);

  const filteredData = Array.isArray(data) ? data.filter(item => {
    if (!search) return true;
    return search.split(' ').every(term =>
      item.label?.toLowerCase().includes(term.trim().toLowerCase())
    );
  }) : [];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={
          isLayout4
            ? 'layout-4 wl4-service-modal max-w-md w-full h-full md:max-h-[90vh] p-0 flex flex-col gap-0 [&>button]:hidden overflow-hidden'
            : 'max-w-md w-full h-full md:max-h-[90vh] p-0 flex flex-col gap-0 [&>button]:hidden overflow-hidden'
        }
        style={modalThemeStyle}
      >
        <DialogTitle className="sr-only">
          {fieldLabels[field]}
        </DialogTitle>

        {isLayout4 ? (
          <>
            <div className="wl4-service-modal-head">
              <div className="wl4-service-modal-head-icon">
                {(() => {
                  const VehicleIcon = getVehicleTypeIcon();
                  return <VehicleIcon className="h-5 w-5" />;
                })()}
              </div>
              <div className="wl4-service-modal-head-text">
                <h2>{fieldLabels[field]}</h2>
                <p>Selecione uma opção</p>
              </div>
              <button
                type="button"
                className="wl4-service-modal-close"
                onClick={() => onOpenChange(false)}
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="wl4-vehicle-search relative shrink-0">
              <Search className="absolute left-7 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--wl4-muted)] z-10" />
              <Input
                type="text"
                placeholder="Pesquisar..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="wl4-service-input pl-10"
              />
            </div>
          </>
        ) : serviceImage ? (
          <div className="relative aspect-[280/100] overflow-hidden flex-shrink-0">
            {/* Close Button */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 h-10 w-10 bg-black/5 hover:bg-black/10 backdrop-blur-sm border border-black/10 text-black z-20"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-5 w-5 text-black" />
            </Button>

            {/* Title and Icon Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-black/5 backdrop-blur-sm border border-black/10">
                  {(() => {
                    const VehicleIcon = getVehicleTypeIcon();
                    return <VehicleIcon className="h-7 w-7 text-black/80" />;
                  })()}
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-black">
                    {fieldLabels[field]}
                  </h2>
                  <p className="text-sm text-black/80">
                    Selecione uma opção
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-slate-100 via-slate-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
            {/* Hidden DialogTitle for accessibility */}
            <DialogTitle className="sr-only">
              {fieldLabels[field]}
            </DialogTitle>

            {/* Gradient Overlay */}
            <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${primaryColor}15, ${primaryColor}05)` }} />
            
            {/* Close Button */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 h-10 w-10 bg-background/10 hover:bg-background/20 backdrop-blur-sm border border-border/20 z-20"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-5 w-5" />
            </Button>

            {/* Title and Icon Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-background/10 backdrop-blur-sm border border-border/20">
                  {(() => {
                    const VehicleIcon = getVehicleTypeIcon();
                    return <VehicleIcon className="h-7 w-7" style={{ color: primaryColor }} />;
                  })()}
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-foreground">
                    {fieldLabels[field]}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Selecione uma opção
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {!isLayout4 && (
        <div className="px-6 pb-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Pesquisar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        )}

        {/* List */}
        <div className={isLayout4 ? 'wl4-service-modal-body !pt-0 flex-1 overflow-y-auto min-h-0' : 'flex-1 overflow-y-auto'}>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className={`h-8 w-8 animate-spin ${isLayout4 ? 'text-[var(--wl4-muted)]' : 'text-muted-foreground'}`} />
            </div>
          ) : filteredData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center px-6 space-y-2">
              <p className={`font-medium ${isLayout4 ? 'text-[var(--wl4-text)]' : 'text-foreground'}`}>
                {search ? 'Nenhum resultado encontrado' : 'Nenhum dado disponível'}
              </p>
              <p className={`text-sm ${isLayout4 ? 'text-[var(--wl4-muted)]' : 'text-muted-foreground'}`}>
                {search ? 'Tente buscar por outro termo' : 'Selecione as opções anteriores primeiro'}
              </p>
            </div>
          ) : (
            <div>
              {filteredData.map((item, index) => {
                const isSelected =
                  (field === 'brand' && fieldValue.brand._id === item._id) ||
                  (field === 'model' && fieldValue.model._id === item._id) ||
                  (field === 'year' && fieldValue.year._id === item._id);

                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => onSelect(field, { _id: item._id, label: item.label })}
                    className={
                      isLayout4
                        ? `wl4-vehicle-list-item wl4-fipe-option-item ${isSelected ? 'is-selected' : ''}`
                        : `relative w-full text-left px-6 py-4 hover:bg-muted/50 transition-all flex items-center justify-between ${isSelected ? 'bg-muted/75' : ''}`
                    }
                  >
                    <p className={`text-sm flex-1 ${isSelected ? 'font-semibold' : 'font-medium'} ${isLayout4 ? 'text-[var(--wl4-text)]' : ''}`}>
                      {item.label}
                    </p>

                    {isSelected && (
                      <Check className="h-5 w-5 shrink-0" style={{ color: isLayout4 ? 'var(--wl4-accent)' : primaryColor }} />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

